import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCountdown } from 'usehooks-ts'
import { useMediaQueryContext } from '../../../../contexts/MediaQueryContext'
import { useTwinData } from '../../../../hooks/storeHooks/useTwin'
import { useMicrophones } from '../../../../hooks/useMicrophones'
import { useSessionWebRTC } from '../../../../hooks/useSessionWebRTC'
import { Controls } from '../../../../pages/SessionPage/Controls/Controls'
import type { WebRTCMessage } from '../../../../types/webRtcMessage'
import { convertSecondsToTime } from '../../../../utils/timeFormat'
import { Heading } from '../Heading'
import { WebCamView } from './WebCamVIew'

const isDev = import.meta.env.MODE === 'development'

interface InterviewTabProps {
  setAudioBlob: (blob: Blob) => void
  onRestart?: () => void
}

const companion = {
  mentorId: '',
  avatarId: '',
}

const MIN_TIME = 540
const MAX_RECORDING_TIME = 600
const START_COUNTDOWN = 180

const InterviewTab: React.FC<InterviewTabProps> = memo(
  ({ setAudioBlob, onRestart }) => {
    const { isSm } = useMediaQueryContext()
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const audioBlobRef = useRef<Blob | null>(null)
    const [avatarReady, setAvatarReady] = useState(false)
    const [avatarTalkingState, setAvatarTalkingState] = useState('')
    const [needInitCamera] = useState(!isSm)
    const [isFinishButtonForced, setIsFinishButtonForced] = useState(false)

    const { linkedInSummary, profileJson, setInterviewSessionId } =
      useTwinData()

    const [count, { startCountdown, stopCountdown }] = useCountdown({
      countStart: MAX_RECORDING_TIME,
      intervalMs: 1000,
    })

    const {
      isMuted,
      muteMicrophone,
      stream: userMicrophoneStream,
      selectedMicrophone,
      audioBlob,
      isRecording,
      connectMicrophone,
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
    } = useMicrophones(false)

    const isRecordingRef = useRef(isRecording)

    const handleWebRTCMessage = (data: WebRTCMessage) => {
      console.log('WebRTC Message:', data)
      switch (data.event) {
        case 'avatar_state_changed':
          if (data.data?.state === 'ready') {
            setAvatarReady(true)
          }
          break
        case 'message_received': {
          const { message } = data.data || {}
          if (
            message?.action === 'completed' &&
            typeof message.content === 'string' &&
            message.role
          ) {
            console.log('Message received:', message.content)
          }
          break
        }
        case 'talk_state_changed':
          handleChangeTalkingState(data.data?.state || '')
          break
        default:
          break
      }
    }

    const { videoStream, audioStream, sessionId, stopWebrtc } =
      useSessionWebRTC({
        userMicrophoneStream,
        selectedMicrophone,
        prevMentorId: null,
        context: '',
        isAuthenticated: false,
        isInterview: true,
        profileData: {
          linkedInSummary,
          profileJson,
        },
        ...companion,
        onMessage: handleWebRTCMessage,
      })

    const isAvatarTalking = useMemo(
      () =>
        !!avatarTalkingState &&
        avatarTalkingState !== 'ended' &&
        avatarTalkingState !== 'canceled',
      [avatarTalkingState],
    )

    const handleChangeTalkingState = (state: string) => {
      setAvatarTalkingState(state)
      console.log('Avatar talking state changed:', state)
      if (state === 'canceled' || state === 'ended') {
        if (!isRecordingRef.current) {
          void startVoiceRecording()
          startCountdown()
        } else {
          resumeRecording()
        }
      }

      if (state === 'started' || state === 'in_progress') {
        pauseRecording()
      }
    }

    const startVoiceRecording = async () => {
      if (isRecordingRef.current) return

      await startRecording()
      startCountdown()
      isRecordingRef.current = true
    }

    const handleRestartInterview = useCallback(() => {
      // stop current recording and countdown
      stopRecording()
      stopCountdown()

      // reset avatar state
      setAvatarReady(false)
      setAvatarTalkingState('')

      // stop WebRTC connection
      stopWebrtc()

      // Call parent to trigger remount
      onRestart?.()
    }, [stopRecording, stopCountdown, stopWebrtc, onRestart])

    const handleStopConnection = useCallback(async () => {
      stopRecording()
      stopCountdown()

      try {
        const blob =
          audioBlob ||
          (await new Promise<Blob>((resolve, reject) => {
            const timeout = setTimeout(
              () => reject(new Error('Timeout waiting for audio blob')),
              5000,
            )
            const checkBlob = () => {
              if (audioBlobRef.current) {
                clearTimeout(timeout)
                resolve(audioBlobRef.current)
              } else {
                setTimeout(checkBlob, 100)
              }
            }
            checkBlob()
          }))

        if (!blob || blob.size === 0) {
          console.error('Invalid audio recording: blob is empty or null')
          throw new Error('Recording failed. Please try again.')
        }
        setAudioBlob(blob)
      } catch (error) {
        console.error('Error processing audio blob:', error)
      } finally {
        stopWebrtc()
      }
    }, [audioBlob, stopRecording, stopCountdown, setAudioBlob, stopWebrtc])

    useEffect(() => {
      audioBlobRef.current = audioBlob
    }, [audioBlob])

    useEffect(() => {
      if (videoRef.current) videoRef.current.srcObject = videoStream || null
    }, [videoStream])

    useEffect(() => {
      if (audioRef.current) audioRef.current.srcObject = audioStream || null
    }, [audioStream])

    useEffect(() => {
      if (!isDev) return

      const handleKeyDown = (event: KeyboardEvent) => {
        console.log('keydown', event)
        // Check for CTRL+CMD+K (Cmd+Ctrl+K on Mac)
        if (event.ctrlKey && event.metaKey && event.key === 'k') {
          event.preventDefault()
          setIsFinishButtonForced(true)
        }
      }

      window.addEventListener('keydown', handleKeyDown)
      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }, [])

    useEffect(() => {
      connectMicrophone().catch(error => {
        console.error('Error connecting microphone:', error)
      })

      return () => {
        stopRecording()
        stopCountdown()
        stopWebrtc()
      }
    }, [])

    useEffect(() => {
      if (count === 0) {
        // Add a buffer time to ensure audio recording finishes processing
        setTimeout(() => {
          handleStopConnection().catch(error => {
            console.error('Error stopping connection:', error)
          })
        }, 1500) // 1.5 seconds buffer to ensure final audio chunk is captured
      }
    }, [count])

    useEffect(() => {
      if (sessionId) {
        setInterviewSessionId(sessionId)
      }
    }, [sessionId])

    return (
      <div className="w-[750px] max-w-full mx-auto mb-auto sm:mb-0">
        <Heading
          title="Teach Your Twin About You"
          subtitle={
            <>
              A quick chat (a few minutes) captures how you explain things so
              answers feel like you.
            </>
          }
        />
        <div className="relative w-full object-cover aspect-video rounded-2xl tc-mv-blur overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            disablePictureInPicture
            data-testid="avatar-main-video"
            className="w-full h-full rounded-2xl"
          />
          {audioStream && <audio ref={audioRef} autoPlay />}
          {videoStream && (
            <>
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-4">
                <Controls
                  isTalking={isAvatarTalking}
                  hasNextSpeech={null}
                  isMuted={isMuted}
                  personaData={null}
                  userMicrophoneStream={userMicrophoneStream}
                  selectedMicrophone={selectedMicrophone}
                  contextParam={null}
                  audioRef={audioRef}
                  isAuthenticated={false}
                  setMuted={muteMicrophone}
                  setOpenAiVoice={() => {}}
                  handleRepeatCurrentSpeech={() => {}}
                  disableStopButton
                  disableEndButton
                  controls
                />
              </div>
              {needInitCamera && <WebCamView />}
            </>
          )}
          {!avatarReady && (
            <div className="start-experience-btn loader !absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <span className="button-content relative">
                <span className="star-icon star-first">✦</span>
                <span className="star-icon star-second large">✦</span>
                <span className="star-icon star-third">✦</span>
                <span className="text ms-2">Connecting...</span>
              </span>
            </div>
          )}
          {count <= START_COUNTDOWN && (
            <div className="w-24 h-12 flex justify-center items-center rounded-2xl tc-mv-blur text-2xl absolute top-4 right-4">
              {convertSecondsToTime(count)}
            </div>
          )}
        </div>

        <div className="flex gap-4 justify-center mt-8 w-full">
          <button
            data-testid="interview-restart-btn"
            className="btn btn-secondary sm:!grow-0 !grow"
            onClick={handleRestartInterview}
            disabled={!avatarReady}
          >
            <i className="fa-solid fa-rotate-right mr-2" />
            Restart
          </button>
          <button
            data-testid="interview-finish-btn"
            className="btn btn-primary sm:!grow-0 !grow"
            onClick={handleStopConnection}
            disabled={
              !isFinishButtonForced && (count > MIN_TIME || !avatarReady)
            }
          >
            Finish Interview
            <i className="fa-solid fa-arrow-right ml-2" />
          </button>
        </div>
      </div>
    )
  },
)

export { InterviewTab }
